import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { RxHeroSlider } from '../components/rx/RxHeroSlider';
import { HealthManagementPanel } from '../components/rx/HealthManagementPanel';
import { careActions, prescriptionActions, wellnessTools } from '../data/rxServices';
import { upcomingRxServices } from '../data/rxServicesUpcoming';
type LocationValue = {
  name: string;
} | null;
interface ServiceCardProps {
  title: string;
  description: string;
  path?: string;
  tone: string;
  icon: React.ElementType<{
    className?: string;
    strokeWidth?: number;
  }>;
  badge?: string;
  disabled?: boolean;
}
function ServiceCard({
  title,
  description,
  path,
  tone,
  icon: Icon,
  badge,
  disabled = false
}: ServiceCardProps) {
  const navigate = useNavigate();
  return <button type="button" disabled={disabled} onClick={() => {
    if (!disabled && path) navigate(path);
  }} className={`group flex min-h-[128px] w-full flex-col rounded-xl border p-3 text-left shadow-[0_2px_10px_rgba(30,43,85,0.05)] transition-[border-color,transform] duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:ring-offset-2 sm:p-4 ${disabled ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60' : 'border-gray-100 bg-white hover:-translate-y-0.5 hover:border-[#cbd8eb]'}`} data-id="element-8875" data-mp="sno792x8ewos">
      <div className="flex w-full items-start justify-between gap-3" data-id="element-8876" data-mp="248uyxg1egvv7">
        <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`} data-id="element-8877" data-mp="247jvjhbv9q4m">
          <Icon className="h-5 w-5" strokeWidth={1.8} data-id="element-8878" data-mp="291xwdz9aspgo" />
        </span>
        {(badge || disabled) && <span className="rounded-md bg-[#eef4ff] px-2 py-1 text-[10px] font-bold tracking-wide text-[#315b96]" data-id="element-8879" data-mp="2itzd8iv892ge">{disabled ? 'COMING SOON' : badge}</span>}
      </div>
      <div className="mt-3" data-id="element-8880" data-mp="29u2ko65pvjg2">
        <h3 className="text-sm font-bold leading-5 text-gray-900" data-id="element-8881" data-mp="29xi1peiyb2k4">{title}</h3>
        <p className="mt-1 hidden text-xs leading-5 text-gray-600 sm:block" data-id="element-8882" data-mp="24vsoptc64ete">{description}</p>
      </div>
      <span className="mt-auto flex items-center gap-1 pt-2 text-xs font-bold text-[#1E2B55]" data-id="element-8883" data-mp="1zysgbbruizz4">
        {disabled ? 'Available soon' : <>Get started <ChevronRightIcon className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" data-id="element-8884" data-mp="1q7ee2w8ifm2a" /></>}
      </span>
    </button>;
}
export function RxHomePage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationValue>(null);
  return <HelmetProvider data-id="element-8885" data-mp="1c9q3qic2jxbh">
      <div className="min-h-screen bg-[#f7f8fb]" data-id="element-8886" data-mp="3nuyxy5u1whug">
        <SEO title="RX Care | Prescriptions, pharmacists & health services" data-id="element-8887" data-mp="3nu73iyc6wq5r" />

        <div className="hidden md:block" data-id="element-8888" data-mp="3sta8kis66wdq">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement data-id="element-8889" data-mp="3sw67oafr7r3r" />
        </div>

        <main className="pb-24 pt-0 md:pb-0 md:pt-[var(--header-height-desktop)]" data-id="element-8890" data-mp="35623o1ynlpc8">
          <div className="md:hidden" data-id="element-8891" data-mp="3a4n4o3lqcd1q">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} transparentAtTop showExperienceSwitcher activeExperience="rx" data-id="element-8892" data-mp="3a1hhwv0kpn96" />
          </div>

          <RxHeroSlider data-id="element-8893" data-mp="2mdr5gley2fkg" />

          <div className="mx-auto max-w-7xl space-y-8 px-4 py-7 md:px-6 md:py-12" data-id="element-8894" data-mp="2mc6jvlaly6yq">
            <HealthManagementPanel data-id="element-8895" data-mp="2qdi2h6v2drxf" />

            <section aria-labelledby="prescription-actions-heading" data-id="element-8896" data-mp="27ag9ixuvto08">
              <div className="flex items-end justify-between gap-4" data-id="element-8897" data-mp="278n0jbwsqrp2">
                <div data-id="element-8898" data-mp="22dg66upf2zct">
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#7155aa]" data-id="element-8899" data-mp="229scnvjz2cy3">I have a prescription</p>
                  <h2 id="prescription-actions-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8900" data-mp="2ca2l72v2aw6x">Manage your prescription</h2>
                </div>
                <button type="button" onClick={() => navigate('/prescription/history')} className="hidden text-sm font-bold text-[#1E2B55] hover:underline sm:inline-flex" data-id="element-8901" data-mp="2pzmnk7kqdrfp">View history</button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4" data-id="element-8902" data-mp="2zxkgv9hg6ef6">
                {prescriptionActions.map(action => <ServiceCard key={action.title} {...action} data-id="element-8903" data-mp="2uyptdu8jc1oq" />)}
              </div>
            </section>

            <section aria-labelledby="care-actions-heading" data-id="element-8904" data-mp="10mj4xtqmroeh">
              <div data-id="element-8905" data-mp="10o3zhjcy37cc">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#287451]" data-id="element-8906" data-mp="10om0suegsbr4">I need help</p>
                <h2 id="care-actions-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8907" data-mp="3cc8vj69wb0p1">Speak with a health professional</h2>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4" data-id="element-8908" data-mp="32jjw59rmdda2">
                {careActions.map(action => <ServiceCard key={action.title} {...action} data-id="element-8909" data-mp="37hlefsh818kh" />)}
              </div>
            </section>

            <section aria-labelledby="upcoming-services-heading" data-id="element-8910" data-mp="2thtvddbul1zm">
              <div data-id="element-8911" data-mp="2xl8kquloeupq">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500" data-id="element-8912" data-mp="2xlrs5hdpa0pi">Coming soon</p>
                <h2 id="upcoming-services-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8913" data-mp="2jms3wau3nntv">More ways we will support your health</h2>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4" data-id="element-8914" data-mp="htkrb95vfkbo">
                {upcomingRxServices.map(service => <ServiceCard key={service.title} {...service} path="" tone="bg-gray-100 text-gray-500" disabled data-id="element-8915" data-mp="mrw1xm3zpyuy" />)}
              </div>
            </section>

            <section aria-labelledby="wellness-tools-heading" data-id="element-8916" data-mp="gcos4oxg6ekz">
              <div data-id="element-8917" data-mp="gb40p86fjbrc">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#5d6482]" data-id="element-8918" data-mp="gf1k7uwx65br">Everyday wellness</p>
                <h2 id="wellness-tools-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8919" data-mp="6eitcg6xtokc">Useful health tools</h2>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4" data-id="element-8920" data-mp="140ybzlb3os9h">
                {wellnessTools.map(tool => <ServiceCard key={tool.title} {...tool} data-id="element-8921" data-mp="z4hkqi8ltxs6" />)}
              </div>
            </section>

            <section className="flex flex-col gap-4 rounded-xl border border-[#d6e4ef] bg-[#eff7fb] p-5 md:flex-row md:items-center md:justify-between md:p-6" data-id="element-8922" data-mp="ub57t5dafff9">
              <div className="flex gap-3" data-id="element-8923" data-mp="1mtalcs068tt4">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white text-[#256a88]" data-id="element-8924" data-mp="1mubwsxl3nibd">
                  <ShieldCheckIcon className="h-5 w-5" data-id="element-8925" data-mp="1i07ni7od2y74" />
                </span>
                <div data-id="element-8926" data-mp="1qchpy37nbiyo">
                  <h2 className="text-base font-bold text-gray-900" data-id="element-8927" data-mp="1qd17hitdw5c6">Licensed care, handled securely</h2>
                  <p className="mt-1 text-sm leading-5 text-gray-600" data-id="element-8928" data-mp="1zbcznp9wfb6q">Your prescription is reviewed by licensed professionals before it is processed.</p>
                </div>
              </div>
              <button type="button" onClick={() => navigate('/medicine-reminder')} className="h-10 flex-shrink-0 rounded-lg border border-[#a8c8d9] bg-white px-4 text-sm font-bold text-[#256a88] transition-colors duration-150 hover:bg-[#f8fdff]" data-id="element-8929" data-mp="2483g4dpm88so">Set medicine reminders</button>
            </section>
          </div>
        </main>

        <div className="hidden md:block" data-id="element-8930" data-mp="2n1h05nrsiuti"><Footer data-id="element-8931" data-mp="2n0fphkeosacb" /></div>
        <MobileFooterNav data-id="element-8932" data-mp="2n31ubca70bqu" />
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={setSelectedLocation} data-id="element-8933" data-mp="2s0af7kmq6tvs" />
      </div>
    </HelmetProvider>;
}